export type AgentType = 'supervisor' | 'micro';

export interface NotionTreeNode {
  id: string;
  parentId: string | null;
  title: string;
}

export interface RegistryTreeResponse {
  manuals: NotionTreeNode[];
  personas: NotionTreeNode[];
}

export interface UpsertManualRequest {
  id?: string;
  pageId: string;
  name: string;
  category: string;
  description: string;
  bestPracticeExecutionIds?: string[];
}

export interface ManualResponse {
  id: string;
  pageId: string;
  name: string;
  category: string;
  description: string;
  bestPracticeExecutionIds?: string[];
  manualMarkdown: string;
  updatedAt: string;
}

export interface UpsertAgentPersonaRequest {
  id?: string;
  pageId: string;
  name: string;
  type: AgentType;
  description: string;
  parentId?: string;
  manualId?: string;
  requiredParams?: Record<string, unknown>;
}

export interface AgentPersonaResponse {
  id: string;
  pageId: string;
  name: string;
  type: AgentType;
  description: string;
  personaMarkdown: string;
  parentId?: string;
  manualId?: string;
  manualMarkdown?: string;
  updatedAt: string;
}

export class AIAgentRegistryClient {
  private readonly headers: HeadersInit;

  constructor(
    private readonly apiUrl: string,
    apiKey: string,
  ) {
    this.headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Origin: window.location.origin,
      'X-API-Key': apiKey,
    };
  }

  public async getRegistryTree(): Promise<RegistryTreeResponse> {
    const response = await fetch(`${this.apiUrl}/agent/registry/tree`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch registry tree: ${response.statusText}`);
    return response.json();
  }

  public async listSupervisorAgents(): Promise<AgentPersonaResponse[]> {
    const response = await fetch(`${this.apiUrl}/agent/persona/supervisors`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch supervisors: ${response.statusText}`);
    return response.json();
  }

  public async listMicroAgents(supervisorId: string): Promise<AgentPersonaResponse[]> {
    const response = await fetch(`${this.apiUrl}/agent/persona/supervisors/${supervisorId}/microAgents`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch micro agents: ${response.statusText}`);
    return response.json();
  }

  public async getAgentPersona(agentId: string): Promise<AgentPersonaResponse> {
    const response = await fetch(`${this.apiUrl}/agent/persona/${agentId}`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch agent: ${response.statusText}`);
    return response.json();
  }

  public async upsertAgentPersona(request: UpsertAgentPersonaRequest): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/persona`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(`Failed to upsert agent: ${response.statusText}`);
  }

  public async deleteAgentPersona(agentId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/persona/${agentId}`, {
      method: 'DELETE',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to delete agent: ${response.statusText}`);
  }

  public async listManualsByCategory(category: string): Promise<ManualResponse[]> {
    const response = await fetch(`${this.apiUrl}/agent/manual/category/${category}`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch manuals: ${response.statusText}`);
    return response.json();
  }

  public async getManual(manualId: string): Promise<ManualResponse> {
    const response = await fetch(`${this.apiUrl}/agent/manual/${manualId}`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch manual: ${response.statusText}`);
    return response.json();
  }

  public async upsertManual(request: UpsertManualRequest): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/manual`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(`Failed to upsert manual: ${response.statusText}`);
  }

  public async deleteManual(manualId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/manual/${manualId}`, {
      method: 'DELETE',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to delete manual: ${response.statusText}`);
  }
}

